/**
 * RWBR — building a whole field of fictional drivers.
 *
 * Sagliano's Part One creates one driver at a time; this is the wrapper that
 * creates a league's worth of them, deterministically, from a single seed.
 *
 * THIS BUILDS A LICENSED ROSTER, NOT A RACE FIELD. The two are different
 * numbers and conflating them is the single easiest way to get Race Week wrong.
 *
 *   RACE WEEK Events Guide, p.2, verbatim:
 *     "Every race has to have a qualifying event, right?  Rather than roll dice
 *      for 50-60 drivers, RED WHITE & BLUE RACIN's 'Race Week' uses a system
 *      that quickly approximates each driver's race week results..."
 *
 * Fifty to sixty drivers show up; forty-three start. RWEG/p.4 STEP SIX counts
 * the field UP to 43 off the bubble stack, and "the drivers in the bubble stack
 * [not] necessary to bring the field to 43" simply do not race. A roster of
 * exactly 43 would make every DNQ impossible, every bubble empty, and STEP SIX
 * a no-op — Race Week would have no teeth at all.
 *
 * So the default is 53: fourteen chartered teams of three (42) plus eleven
 * independents, which lands inside the booklet's own "50-60" and leaves ten
 * drivers a week fighting for the last handful of spots. Sagliano's payout,
 * points and re-rating tables are all still built for 43 FINISHING positions
 * ("43rd & below", pp. 17, 23-24, 28, 32) — that is the field, and `raceWeek.ts`
 * caps it there. `size` remains an option: `buildField(seed, { size: 43 })`
 * behaves exactly as it always did.
 *
 * CAR NUMBERS ARE STRINGS AND THEY REPEAT. The published sets carry `0`, the
 * zero-padded `02` and `07`, and a bare `X` with no digits at all, and the same
 * number appears on several cards in one set (`26 X` on five of the 106 sample
 * cards). Nothing may be keyed on a car number — `driverId` is the identity.
 *
 * THREE RANDOM STREAMS, ON PURPOSE. Identity (name, number) is drawn from a
 * separate stream derived from the same seed, so that editing the name pools
 * cannot shift a single dice roll on any driver's card. The card rolls come off
 * one shared `Dice`, in driver order, exactly as if a human sat down and rolled
 * the field one card at a time. The garage — who owns which car, and what the
 * shop is called — comes off a THIRD stream, added later, for the same reason:
 * chartering the field must not repaint a single rating. The team pass runs
 * after every card is rolled and only writes `teamId`.
 *
 * THE GARAGE. Fourteen chartered teams of three, and whatever is left over runs
 * independent — 42 of the 53 licensed drivers chartered and eleven privateers,
 * which is close enough to a real Cup entry list to feel right. The privateers
 * are the ones who spend Race Week on the bubble.
 *
 * Teams are not an invention of this file. D-007 already made the pit crew
 * grade a TEAM attribute ("Independents carry their own"), which means the
 * league has owners whether or not anything has drawn them yet; this is where
 * they get built. Today a team carries a livery family and a manufacturer
 * (`src/domain/livery.ts`); it is also the object career money and crew
 * investment will eventually land on.
 *
 * COLOUR IS DEALT, NOT HASHED. The charter asks `assignInks` for one base per
 * shop, in team order, and no two shops in a field come out the same colour
 * while the palette has an unused one left. A team's cars then all carry that
 * base and that shop's make; only accent, scheme and decal vary between them.
 */

import { Dice, makeRng } from './dice.ts';
import { createDriver, DEFAULT_GEN_PROFILE, type GenProfile, type GeneratedDriver } from './driverGen.ts';
import { assignInks, liveryFor, type InkName, type Livery, type Manufacturer } from './livery.ts';
import { applyTraits, assignTraits, type TraitAssignment } from './traits.ts';

/**
 * The licensed roster: how many drivers hold a licence and turn up to try to
 * qualify. RWEG/p.2's own "50-60 drivers", resolved to 14 charter teams x 3 = 42
 * plus 11 independents.
 */
export const DEFAULT_ROSTER_SIZE = 53;

/**
 * The maximum STARTING field — what RWEG/p.4 STEP SIX rounds the field out to,
 * and what every Sagliano payout and re-rating table is built for. This is NOT
 * the roster size; `raceWeek.ts` is what caps a grid at it.
 */
export const MAX_STARTING_FIELD = 43;

/**
 * `buildField`'s default size. Kept under its original name so every existing
 * caller and test that reads the constant keeps working; it now names the
 * roster, because that is what `buildField` builds.
 */
export const DEFAULT_FIELD_SIZE = DEFAULT_ROSTER_SIZE;

// ---------------------------------------------------------------------------
// Name and number pools
// ---------------------------------------------------------------------------

/**
 * Fictional given names in the register of the published fictional sets
 * (SCRAM, MoSSCh, the EZRA rosters): short, southern-inflected, a lot of
 * nicknames-as-names. No real driver is named here.
 */
export const FIRST_NAMES: readonly string[] = [
  'Arlen', 'Bo', 'Boone', 'Brady', 'Buck', 'Buddy', 'Cale', 'Carl', 'Cass', 'Chase',
  'Chip', 'Clay', 'Cody', 'Colt', 'Cooter', 'Curtis', 'Dale', 'Dean', 'Del', 'Denny',
  'Dewey', 'Dusty', 'Earl', 'Elmo', 'Flint', 'Floyd', 'Frankie', 'Gus', 'Hank', 'Harlan',
  'Hoyt', 'Jeb', 'Jesse', 'Jimmy', 'Junior', 'Kip', 'Lonnie', 'Luther', 'Mack', 'Marty',
  'Merle', 'Mooch', 'Otis', 'Pepper', 'Rance', 'Randy', 'Rip', 'Roscoe', 'Rusty', 'Shad',
  'Skeet', 'Sonny', 'Stu', 'Tad', 'Travis', 'Tuck', 'Vern', 'Wade', 'Wiley', 'Zeke',
];

/** Fictional surnames, same register. */
export const SURNAMES: readonly string[] = [
  'Ackerd', 'Ballew', 'Barkley', 'Batson', 'Beasley', 'Bexley', 'Blanchard', 'Bodine',
  'Brannock', 'Bricker', 'Brumley', 'Calloway', 'Carrick', 'Cheatwood', 'Clatterbuck',
  'Coffey', 'Corliss', 'Crickmore', 'Cutshaw', 'Dandridge', 'Darnell', 'Deaton',
  'Dockery', 'Dunkle', 'Eddleman', 'Farris', 'Fentress', 'Ficklin', 'Gambill', 'Gantt',
  'Gilliam', 'Grubbs', 'Hagerman', 'Halstead', 'Hargrove', 'Hasty', 'Hatley', 'Hinkle',
  'Holcomb', 'Huddleston', 'Ingle', 'Jessup', 'Kettering', 'Kilgore', 'Ledbetter',
  'Lightner', 'Lockridge', 'Lunsford', 'Maddox', 'Marchbanks', 'Meacham', 'Millsap',
  'Nabors', 'Ogletree', 'Overcash', 'Parham', 'Pennington', 'Poteat', 'Quillen',
  'Rasmussen', 'Redfern', 'Rhinehart', 'Ricketts', 'Rountree', 'Sadler', 'Scoggins',
  'Shrader', 'Slocum', 'Stroupe', 'Swaim', 'Tatum', 'Thackston', 'Tolliver', 'Trantham',
  'Turnage', 'Vance', 'Waddell', 'Whitaker', 'Wingo', 'Yeargin', 'Yount', 'Zachary',
];

/**
 * Car numbers as they actually print. Includes the three shapes the sample set
 * proves are legal and that an integer column would destroy: the plain `0`, the
 * zero-padded `02` / `07`, and the bare `X`.
 */
export const CAR_NUMBERS: readonly string[] = [
  '0', '02', '07', 'X',
  '1', '2', '3', '4', '5', '6', '7', '8', '9', '10',
  '11', '12', '14', '15', '16', '17', '18', '19', '20', '21',
  '22', '23', '24', '25', '26', '27', '28', '29', '30', '31',
  '32', '33', '34', '36', '38', '39', '40', '41', '42', '43',
  '44', '45', '46', '47', '48', '50', '51', '52', '54', '55',
  '56', '60', '61', '63', '66', '68', '71', '74', '75', '77',
  '78', '80', '81', '83', '86', '88', '90', '91', '92', '95',
  '96', '97', '98', '99',
];

// ---------------------------------------------------------------------------
// The garage
// ---------------------------------------------------------------------------

/** Chartered teams in a full field. The rest of the entry list runs independent. */
export const CHARTER_TEAMS = 14;
/** Cars per chartered team. */
export const TEAM_SIZE = 3;

/**
 * Trades a short-track sponsor actually is. A stock car team is named after
 * the shop that pays for it, and the shop makes something you can put on a
 * truck door.
 */
export const TRADES: readonly string[] = [
  'Ironworks', 'Sawmill', 'Blacktop', 'Diesel', 'Hardware', 'Feed & Seed',
  'Auto Parts', 'Tool & Die', 'Machine Shop', 'Salvage', 'Freight', 'Paving',
  'Lumber', 'Cattle', 'Poultry', 'Textile', 'Bottling', 'Furniture',
  'Fuel Oil', 'Roofing', 'Trucking', 'Wrecker', 'Speed Shop', 'Concrete',
  'Well Drilling', 'Grain', 'Propane', 'Body Shop', 'Radiator', 'Cotton Gin',
];

export interface Team {
  /** `tm-01`, `tm-02`, … Stable for a given seed and field size. */
  teamId: string;
  /** The shop's name, as it would be painted on the hauler. */
  name: string;
  /** Roster, in the order the team's liveries are varied. */
  driverIds: string[];
  /**
   * The base colour this shop was dealt. Distinct from every other shop's in
   * the field while the palette has room — twenty-one bases against fourteen
   * charters, so a full field never repeats. Anyone painting one of this team's
   * cars must pass this to `liveryForDriver`, or they will get the shop's
   * un-dealt hashed colour instead.
   */
  baseInk: InkName;
  /** The family every car in this shop is painted out of. */
  livery: Livery;
}

// ---------------------------------------------------------------------------
// Building the field
// ---------------------------------------------------------------------------

export interface FieldOptions {
  /**
   * How many drivers to license. Default 53 (`DEFAULT_ROSTER_SIZE`) — the
   * roster, not the starting field. Pass `{ size: 43 }` for the old behaviour.
   */
  size?: number;
  /** Driver ids are `${idPrefix}-001`, `-002`, ... Default 'drv'. */
  idPrefix?: string;
  /** Team ids are `${teamIdPrefix}-01`, `-02`, ... Default 'tm'. */
  teamIdPrefix?: string;
  firstNames?: readonly string[];
  surnames?: readonly string[];
  carNumbers?: readonly string[];
  trades?: readonly string[];
  /** Chartered teams to fill, before the field size caps it. Default 14. */
  charterTeams?: number;
  /**
   * Which driver-generation table set every card in this field is rolled on.
   * Default `calibrated` (D-021). `'sagliano'` builds a field on the printed
   * guide's own numbers — kept switchable so the faithful version is never lost.
   */
  profile?: GenProfile;
  /**
   * Fraction of the roster carrying an EZRA driver trait (EZRA-S2 §4.0).
   * Default `DEFAULT_TRAIT_SHARE`; pass 0 for a plain PLAAY field with no
   * traits at all, which is what every published card set actually is.
   */
  traitShare?: number;
}

/**
 * A generated driver plus the one thing the garage adds to him that is not on
 * the printed card: the make he runs.
 *
 * It is declared HERE rather than on `DriverCard` because it is not a card
 * field — nothing in the engine reads it, no table is keyed on it, and a saved
 * card without it is still a complete card. It extends `GeneratedDriver`, so
 * every existing consumer of `field.drivers` keeps working untouched.
 */
export interface FieldDriver extends GeneratedDriver {
  /**
   * The make painted on this car's nose tab. Every car in a shop shares one —
   * a team runs one manufacturer. Independents get their own.
   */
  manufacturer: Manufacturer;
}

export interface Field {
  seed: number;
  /** The table set every card in this field was rolled on. */
  profile: GenProfile;
  /** How many drivers were licensed. NOT the starting field — see the header. */
  size: number;
  drivers: FieldDriver[];
  /** The chartered garage. Drivers not on one of these are independents. */
  teams: Team[];
  /** Which drivers were dealt an EZRA trait, and under which ruling. */
  traits: TraitAssignment;
}

/**
 * Build a complete field. Same seed, same field — every rating, every name,
 * every car number, in the same order.
 */
export function buildField(seed: number, options: FieldOptions = {}): Field {
  const size = options.size ?? DEFAULT_FIELD_SIZE;
  const idPrefix = options.idPrefix ?? 'drv';
  const teamIdPrefix = options.teamIdPrefix ?? 'tm';
  const firstNames = options.firstNames ?? FIRST_NAMES;
  const surnames = options.surnames ?? SURNAMES;
  const carNumbers = options.carNumbers ?? CAR_NUMBERS;
  const trades = options.trades ?? TRADES;
  const charterTeams = options.charterTeams ?? CHARTER_TEAMS;
  const profile = options.profile ?? DEFAULT_GEN_PROFILE;

  if (!Number.isInteger(size) || size < 1) {
    throw new RangeError(`field size must be a positive integer, got ${size}`);
  }
  if (firstNames.length === 0 || surnames.length === 0 || carNumbers.length === 0) {
    throw new RangeError('field pools (first names, surnames, car numbers) must be non-empty');
  }
  if (!Number.isInteger(charterTeams) || charterTeams < 0) {
    throw new RangeError(`charter teams must be a non-negative integer, got ${charterTeams}`);
  }
  if (trades.length === 0) {
    throw new RangeError('the trade pool must be non-empty');
  }
  if (size > firstNames.length * surnames.length) {
    throw new RangeError(
      `cannot build ${size} distinct names from ${firstNames.length} first names × ` +
        `${surnames.length} surnames`,
    );
  }
  if (size > carNumbers.length) {
    throw new RangeError(
      `cannot build ${size} distinct car numbers from a pool of ${carNumbers.length}`,
    );
  }

  // Card rolls: one shared die, driver by driver, like a human doing it by hand.
  const dice = new Dice(seed);
  // Identity: a separate stream off the same seed, so changing the name pools
  // never moves a rating.
  const idRng = makeRng((seed ^ 0x9e3779b9) >>> 0);
  const pick = <T>(pool: readonly T[]): T => pool[Math.floor(idRng() * pool.length)];

  const usedNames = new Set<string>();
  const usedNumbers = new Set<string>();
  const drivers: GeneratedDriver[] = [];

  for (let i = 0; i < size; i++) {
    const name = nextName(pick, firstNames, surnames, usedNames);
    usedNames.add(name);
    const carNumber = nextNumber(pick, carNumbers, usedNumbers);
    usedNumbers.add(carNumber);
    drivers.push(
      createDriver(dice, {
        driverId: `${idPrefix}-${String(i + 1).padStart(3, '0')}`,
        name,
        carNumber,
        profile,
      }),
    );
  }

  // Third stream. Runs only after every card is rolled, and writes nothing but
  // `teamId` — chartering the field cannot move a rating.
  const teams = charterField(seed, drivers, {
    teamIdPrefix,
    surnames,
    trades,
    charterTeams,
  });

  // A shop runs one make, so a driver's manufacturer is his team's. An
  // independent buys his own, off his own id. Read off the livery rather than
  // rolled again, so the tab on the car and the make in the data can never
  // disagree.
  // FOURTH STREAM. Traits are dealt after every card is rolled and every shop is
  // chartered, off their own salt — so turning traits on, or moving the share,
  // cannot shift a single rating, name, number or livery on an existing seed.
  //
  // The inaugural roster is NOT treated as 53 first-year rookies, so EZRA's
  // "1st year Rookie drivers will not have any traits" does not empty it
  // (D-EZ-3). These are the drivers a league is founded WITH — they carry gold
  // trophies and career-shaped ratings; Sagliano rolls them on the inaugural
  // tables because there is no prior season to re-rate from, not because they
  // are rookies. Drivers who genuinely debut later — signed mid-season to
  // replace a dead driver, or licensed over a winter — are rookies in the sense
  // EZRA means, and are excluded where they are created.
  const traits = assignTraits(seed, drivers.map((d) => d.card), { share: options.traitShare });
  const carded = applyTraits(drivers.map((d) => d.card), traits);
  for (let i = 0; i < drivers.length; i++) drivers[i] = { ...drivers[i], card: carded[i] };

  const shopOf = new Map(teams.map((t) => [t.teamId, t]));
  const painted: FieldDriver[] = drivers.map((d) => {
    const shop = d.card.teamId ? shopOf.get(d.card.teamId) : undefined;
    return {
      ...d,
      manufacturer: (shop?.livery ?? liveryFor(d.card.driverId)).manufacturer,
    };
  });

  return { seed, size, profile, drivers: painted, teams, traits };
}

// ---------------------------------------------------------------------------
// Chartering
// ---------------------------------------------------------------------------

interface CharterOptions {
  teamIdPrefix: string;
  surnames: readonly string[];
  trades: readonly string[];
  charterTeams: number;
}

/**
 * Hand the field out to team owners, in place, and hand each owner a livery.
 *
 * THE ROSTERS ARE SHUFFLED, not sliced off the top. Driver order is generation
 * order and the starting grid is drawn from it, so slicing would seat all three
 * of a team's cars on consecutive rows of the grid, every race, forever.
 *
 * A roster too small for the full charter fills as many whole teams as it can
 * (`floor(size / 3)`) and the remainder run independent — the default 53 gives
 * the intended 14 teams and eleven privateers.
 */
function charterField(
  seed: number,
  drivers: GeneratedDriver[],
  options: CharterOptions,
): Team[] {
  const teamCount = Math.min(options.charterTeams, Math.floor(drivers.length / TEAM_SIZE));
  if (teamCount <= 0) return [];

  const rng = makeRng((seed ^ 0x85ebca6b) >>> 0);
  const pick = <T>(pool: readonly T[]): T => pool[Math.floor(rng() * pool.length)];

  const seats = drivers.map((_, i) => i);
  for (let i = seats.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [seats[i], seats[j]] = [seats[j], seats[i]];
  }

  const usedNames = new Set<string>();
  const teams: Team[] = [];

  // Deal the palette out across the whole charter BEFORE painting anybody, so
  // no two shops in this field end up the same colour. Hashing each team id
  // straight into the palette is what once put six purple cars on one board.
  const teamIds = Array.from(
    { length: teamCount },
    (_, t) => `${options.teamIdPrefix}-${String(t + 1).padStart(2, '0')}`,
  );
  const inks = assignInks(teamIds);

  for (let t = 0; t < teamCount; t++) {
    const teamId = teamIds[t];
    const roster = seats.slice(t * TEAM_SIZE, (t + 1) * TEAM_SIZE);
    const name = nextTeamName(pick, options.surnames, options.trades, usedNames);
    usedNames.add(name);

    for (const seat of roster) {
      drivers[seat] = {
        ...drivers[seat],
        card: { ...drivers[seat].card, teamId },
      };
    }

    teams.push({
      teamId,
      name,
      driverIds: roster.map((seat) => drivers[seat].card.driverId),
      baseInk: inks[t],
      livery: liveryFor(teamId, undefined, inks[t]),
    });
  }

  return teams;
}

/**
 * The shop's name. Five shapes, all of them things a real garage is called:
 * two owners on the door, the trade that pays the bills, a family, one man, or
 * one man and a lawyer.
 */
function nextTeamName(
  pick: <T>(pool: readonly T[]) => T,
  surnames: readonly string[],
  trades: readonly string[],
  used: Set<string>,
): string {
  const twoOwners = () => `${pick(surnames)} & ${pick(surnames)} Motorsports`;
  const theTrade = () => `${pick(trades)} Racing`;
  const theFamily = () => `${pick(surnames)} Brothers`;
  // Weighted by repetition: the three shapes above are what a stock car garage
  // is usually called, and an unweighted list turns half the field into
  // "<Surname> Motorsports".
  const shapes: readonly (() => string)[] = [
    twoOwners, twoOwners,
    theTrade, theTrade,
    theFamily,
    () => `${pick(surnames)} Motorsports`,
    () => `${pick(trades)} ${pick(surnames)} Racing`,
  ];

  for (let attempt = 0; attempt < 200; attempt++) {
    const candidate = pick(shapes)();
    // `Vance & Vance Motorsports` is a typo, not a team.
    if (/^(.+) & \1 Motorsports$/.test(candidate)) continue;
    if (!used.has(candidate)) return candidate;
  }
  for (const trade of trades) {
    for (const surname of surnames) {
      const candidate = `${trade} ${surname} Racing`;
      if (!used.has(candidate)) return candidate;
    }
  }
  throw new Error('team name pools exhausted');
}

/**
 * A car number not already in the field.
 *
 * THIS USED TO DRAW WITH REPLACEMENT, on the reasoning that the published
 * 2021 card set contains duplicate numbers and so duplicates are correct. Half
 * right, and the wrong half was load-bearing: that set is 106 cards spanning a
 * whole season, in which one car is driven by several drivers at different
 * races. No single 43-car RACE ever has two of the same number.
 *
 * Our roster is not a card set — it is 53 drivers who all go to the same track
 * on the same weekend, 43 of whom start. Drawing with replacement put two #15s
 * and two #18s on the same board, which is not a card-set property, it is a
 * broken race. Found by the off-season suite, which was the first thing in the
 * project to assert uniqueness across a roster.
 *
 * Same shape as `nextName` below: random first, and only walk the pool if the
 * random draw keeps colliding, so the common case stays random and the
 * pathological case still terminates.
 */
function nextNumber(
  pick: <T>(pool: readonly T[]) => T,
  carNumbers: readonly string[],
  used: Set<string>,
): string {
  for (let attempt = 0; attempt < 200; attempt++) {
    const candidate = pick(carNumbers);
    if (!used.has(candidate)) return candidate;
  }
  for (const candidate of carNumbers) if (!used.has(candidate)) return candidate;
  throw new RangeError(
    `car number pool exhausted: ${carNumbers.length} numbers for a larger field`,
  );
}

/**
 * A full name not already in the field. Random first, and only if the random
 * draw keeps colliding does it walk the cross product — so the common case
 * stays random and the pathological case still terminates.
 */
function nextName(
  pick: <T>(pool: readonly T[]) => T,
  firstNames: readonly string[],
  surnames: readonly string[],
  used: Set<string>,
): string {
  for (let attempt = 0; attempt < 64; attempt++) {
    const candidate = `${pick(firstNames)} ${pick(surnames)}`;
    if (!used.has(candidate)) return candidate;
  }
  for (const surname of surnames) {
    for (const first of firstNames) {
      const candidate = `${first} ${surname}`;
      if (!used.has(candidate)) return candidate;
    }
  }
  throw new Error('name pools exhausted — every first-name/surname pair is already in the field');
}
